import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchDashboardStats, setStatsLoading } from "../../../core/data/redux/slices/DashboardStatsSlice";
import {
  IoPeopleOutline,
  IoChatbubblesOutline,
  IoCalendarOutline,
  IoCalendarNumberOutline,
  IoAddCircleOutline,
} from "react-icons/io5";
import { all_routes } from "../../router/all_routes";
import dayjs from "dayjs";
import "./DashboardStats.css";

const route = all_routes;

const DashboardStats = ({ role, startDate, endDate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    totalLeads,
    totalWabaChats,
    totalMeetings,
    todayMeetings,
    todayMeetingsCount,
    loading,
    error,
  } = useSelector((state) => state.dashboardStats);

  useEffect(() => {
    dispatch(fetchDashboardStats({ startDate, endDate }));
  }, [dispatch, startDate, endDate]);

  // Retry once on network/CORS failure caused by Lambda cold-start
  const retryRef = useRef(false);
  useEffect(() => {
    if (!error) { retryRef.current = false; return; }
    if (retryRef.current) return;
    retryRef.current = true;
    dispatch(setStatsLoading());
    const t = setTimeout(() => dispatch(fetchDashboardStats({ startDate, endDate })), 2000);
    return () => clearTimeout(t);
    // `loading` intentionally omitted: dispatching setStatsLoading() here would change
    // `loading`, trigger cleanup, and cancel the timer before it fires.
  }, [error, startDate, endDate, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const calendarPath = role === "agent" ? route.agentCalendar : route.calendar;

  const handleAddMeeting = () => {
    navigate(`${calendarPath}?action=create-event`);
  };

  // Dynamic label for the meetings card
  const todayStr = dayjs().format("YYYY-MM-DD");
  const isSingleDay = startDate === endDate;
  const isToday = isSingleDay && startDate === todayStr;
  const meetingsLabel = isToday
    ? "Today's Meetings"
    : isSingleDay
    ? dayjs(startDate).format("MMM D") + " Meetings"
    : "Meetings in Period";

  return (
    <div className="ds-grid">
      {/* Total Leads */}
      <div className="ds-card ds-card--leads">
        <div className="ds-top">
          <span className="ds-label">Total Leads</span>
          <div className="ds-icon">
            <IoPeopleOutline />
          </div>
        </div>
        {loading ? (
          <div className="ds-skeleton-val" />
        ) : (
          <div className="ds-value">{totalLeads.toLocaleString()}</div>
        )}
      </div>

      {/* Total WABA Chats */}
      <div className="ds-card ds-card--waba">
        <div className="ds-top">
          <span className="ds-label">WhatsApp Chats</span>
          <div className="ds-icon">
            <IoChatbubblesOutline />
          </div>
        </div>
        {loading ? (
          <div className="ds-skeleton-val" />
        ) : (
          <div className="ds-value">{totalWabaChats.toLocaleString()}</div>
        )}
      </div>

      {/* Period Meetings */}
      <div className="ds-card ds-card--today">
        <div className="ds-top">
          <span className="ds-label">{meetingsLabel}</span>
          <div className="ds-icon">
            <IoCalendarOutline />
          </div>
        </div>
        {loading ? (
          <div className="ds-skeleton-val" />
        ) : todayMeetingsCount === 0 ? (
          isToday ? (
            <button className="ds-add-meeting-btn" onClick={handleAddMeeting}>
              <IoAddCircleOutline style={{ fontSize: 15 }} />
              Add Meeting
            </button>
          ) : (
            <div className="ds-value">0</div>
          )
        ) : (
          <div className="ds-meeting-list">
            {todayMeetings.map((m) => (
              <div className="ds-meeting-item" key={m.meeting_id}>
                <div className="ds-meeting-dot" />
                <div className="ds-meeting-info">
                  <div className="ds-meeting-title">{m.meetingTitle}</div>
                  <div className="ds-meeting-meta">
                    {m.contactName && `${m.contactName} · `}
                    {!isSingleDay && m.meetingStartDate
                      ? `${dayjs(m.meetingStartDate).format("MMM D")} · `
                      : ""}
                    {m.meetingStartTime || ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Total Meetings */}
      <div className="ds-card ds-card--total">
        <div className="ds-top">
          <span className="ds-label">Total Meetings</span>
          <div className="ds-icon">
            <IoCalendarNumberOutline />
          </div>
        </div>
        {loading ? (
          <div className="ds-skeleton-val" />
        ) : (
          <div className="ds-value">{totalMeetings.toLocaleString()}</div>
        )}
      </div>
    </div>
  );
};

export default DashboardStats;
